/* eslint-disable no-undef */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuthenticatedFetch } from './useAuthenticatedFetch';
import { useCurrentUser } from './useApi';
import { marketAccessCache } from '../services/market-access-cache';
import { API_URL } from '../utils/config';

interface MarketAccessInfo {
  hasAccess: boolean;
  accessLevel?: 'market' | 'country' | 'continent';
  validUntil?: string;
}

interface MarketAccessState {
  [marketId: number]: MarketAccessInfo;
}

interface UseMarketAccessOptions {
  enabled?: boolean;
  refetchInterval?: number;
  onError?: (error: Error) => void;
}

export function useMarketAccess(
  marketIds: number[],
  options: UseMarketAccessOptions = {}
) {
  const { enabled = true, refetchInterval, onError } = options;
  const [accessState, setAccessState] = useState<MarketAccessState>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { data: user } = useCurrentUser();
  const abortControllerRef = useRef<AbortController>();
  const refetchIntervalRef = useRef<number>();

  // Memoize market IDs to prevent unnecessary re-renders
  const marketIdsString = marketIds.join(',');
  const memoizedMarketIds = useMemo(
    () => [...marketIds].sort((a, b) => a - b),
    [marketIdsString]
  );

  const checkAccess = useCallback(async (bypassCache = false) => {
    if (!user || !enabled || memoizedMarketIds.length === 0) return;

    // Cancel any in-flight requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);

    try {
      // Check cache first unless bypassing
      if (!bypassCache) {
        const cachedState: MarketAccessState = {};
        const uncachedIds: number[] = [];

        for (const marketId of memoizedMarketIds) {
          const cached = marketAccessCache.get(user.id, marketId);
          if (cached) {
            cachedState[marketId] = {
              hasAccess: cached.hasAccess,
              accessLevel: cached.accessLevel as any,
              validUntil: cached.validUntil,
            };
          } else {
            uncachedIds.push(marketId);
          }
        }

        // If all are cached, return immediately
        if (uncachedIds.length === 0) {
          setAccessState(cachedState);
          setLoading(false);
          return;
        }

        // Set partial state with cached values for immediate UI update
        if (Object.keys(cachedState).length > 0) {
          setAccessState(prev => ({ ...prev, ...cachedState }));
        }

        // Fetch only uncached markets
        if (uncachedIds.length > 0) {
          const response = await authenticatedFetch(
            `${API_URL}/api/marketcompass/subscriptions/check-access`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ marketIds: uncachedIds }),
            }
          );

          if (!response) {
            throw new Error('Failed to get authenticated response');
          }

          if (!response.ok) {
            throw new Error(`Failed to check access: ${response.statusText}`);
          }

          const data = await response.json();
          const newState = { ...cachedState };
          
          data.marketAccess.forEach((access: any) => {
            newState[access.marketId] = {
              hasAccess: access.hasAccess,
              accessLevel: access.accessLevel,
              validUntil: access.validUntil?.toString(),
            };
            
            // Update cache
            marketAccessCache.set(
              user.id,
              access.marketId,
              access.hasAccess,
              access.accessLevel,
              access.validUntil?.toString()
            );
          });

          setAccessState(newState);
        }
      } else {
        // Bypass cache - fetch all
        const response = await authenticatedFetch(
          `${API_URL}/api/marketcompass/subscriptions/check-access`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ marketIds: memoizedMarketIds }),
          }
        );

        if (!response) {
          throw new Error('Failed to get authenticated response');
        }

        if (!response.ok) {
          throw new Error(`Failed to check access: ${response.statusText}`);
        }

        const data = await response.json();
        const newState: MarketAccessState = {};
        
        data.marketAccess.forEach((access: any) => {
          newState[access.marketId] = {
            hasAccess: access.hasAccess,
            accessLevel: access.accessLevel,
            validUntil: access.validUntil?.toString(),
          };
          
          // Update cache
          marketAccessCache.set(
            user.id,
            access.marketId,
            access.hasAccess,
            access.accessLevel,
            access.validUntil?.toString()
          );
        });

        setAccessState(newState);
      }
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        setError(err);
        onError?.(err);
      }
    } finally {
      setLoading(false);
    }
  }, [memoizedMarketIds, user, enabled, authenticatedFetch, onError]);

  // Initial load and market ID changes
  useEffect(() => {
    if (!user || !enabled || memoizedMarketIds.length === 0) return;

    checkAccess();
    
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [memoizedMarketIds, user, enabled]);

  // Set up refetch interval if specified
  useEffect(() => {
    if (refetchInterval && refetchInterval > 0) {
      refetchIntervalRef.current = window.setInterval(() => {
        checkAccess(true); // Bypass cache on interval refetch
      }, refetchInterval);

      return () => {
        if (refetchIntervalRef.current) {
          window.clearInterval(refetchIntervalRef.current);
        }
      };
    }
  }, [refetchInterval, checkAccess]);

  const hasAccessToMarket = useCallback((marketId: number): boolean => {
    return accessState[marketId]?.hasAccess || false;
  }, [accessState]);

  const getAccessLevel = useCallback((marketId: number): string | undefined => {
    return accessState[marketId]?.accessLevel;
  }, [accessState]);

  const getValidUntil = useCallback((marketId: number): Date | undefined => {
    const validUntil = accessState[marketId]?.validUntil;
    return validUntil ? new Date(validUntil) : undefined;
  }, [accessState]);

  const invalidateCache = useCallback(() => {
    if (user) {
      marketAccessCache.invalidate(user.id);
      checkAccess(true); // Bypass cache on refetch
    }
  }, [user, checkAccess]);

  const refetch = useCallback(() => {
    return checkAccess(true);
  }, [checkAccess]);

  return {
    accessState,
    hasAccessToMarket,
    getAccessLevel,
    getValidUntil,
    loading,
    error,
    refetch,
    invalidateCache,
    isChecking: loading && Object.keys(accessState).length === 0,
    isRefetching: loading && Object.keys(accessState).length > 0,
  };
}

// Convenience hook for single market
export function useSingleMarketAccess(
  marketId: number | undefined,
  options?: UseMarketAccessOptions
) {
  const marketIds = useMemo(() => marketId ? [marketId] : [], [marketId]);
  const result = useMarketAccess(marketIds, options);

  return {
    hasAccess: marketId ? result.hasAccessToMarket(marketId) : false,
    accessLevel: marketId ? result.getAccessLevel(marketId) : undefined,
    validUntil: marketId ? result.getValidUntil(marketId) : undefined,
    loading: result.loading,
    error: result.error,
    refetch: result.refetch,
  };
}